import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useQuote from "../viewmodels/useQuote";
import { useMediaFallback, useMediaReady, primeVideo } from "./mediaFallback";
import { nextIndex } from "../utils/navigation";
import styled, { keyframes } from "styled-components";

export const ITEMS_PER_PAGE = 12;
const MIN_SPINNER_MS = 1500; // hold the spinner long enough to read the quote
const STAGGER_MS = 130; // heartbeat delay between each card's reveal

/**
 * The shared photo-viewing experience: a paginated grid of lazily-loaded cards,
 * a keyboard/swipe/focus-trapped lightbox, the loading spinner with a lyric
 * quote, and windowed pagination. The Gallery feeds it every published key in
 * descending order; a Collection feeds it its curated list. Both look and behave
 * identically.
 *
 * @param imageKeys full ordered key array, or null while the parent is loading
 * @param currentPage 1-based page from the URL
 * @param basePath    URL base for pagination links (e.g. "/gallery" or
 *                    "/collections/<id>"); page N links to `${basePath}/${N}`
 */
const PhotoGallery = ({ imageKeys, currentPage, basePath }) => {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [ready, setReady] = useState(false);
  // Fresh quote per page — PhotoGallery stays mounted across page navigation.
  const loadingText = useQuote(currentPage);
  const [dots, setDots] = useState(".");

  const totalPages = imageKeys ? Math.max(1, Math.ceil(imageKeys.length / ITEMS_PER_PAGE)) : 1;
  const pageKeys = imageKeys
    ? imageKeys.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
    : [];

  // Animate the dots only while the spinner is up.
  useEffect(() => {
    if (ready) return undefined;
    const id = setInterval(() => setDots((d) => (d.length === 3 ? "." : d + ".")), 500);
    return () => clearInterval(id);
  }, [ready]);

  // Hold the spinner at least MIN_SPINNER_MS after the keys arrive so the quote
  // is readable, then reveal the grid. Re-runs on page change.
  useEffect(() => {
    setReady(false);
    setSelectedIndex(null);
    if (!imageKeys) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      if (!cancelled) setReady(true);
    }, MIN_SPINNER_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [imageKeys, currentPage]);

  // A page past the end (bookmark / stale link) redirects to the last page.
  useEffect(() => {
    if (imageKeys && currentPage > totalPages) {
      navigate(`${basePath}/${totalPages}`, { replace: true });
    }
  }, [imageKeys, currentPage, totalPages, basePath, navigate]);

  const modalRef = React.useRef(null);
  const closeModal = useCallback(() => setSelectedIndex(null), []);
  const move = useCallback(
    (direction) =>
      setSelectedIndex((i) => (i === null ? i : nextIndex(i, direction, pageKeys.length))),
    [pageKeys.length],
  );

  useEffect(() => {
    if (selectedIndex === null) return undefined;
    const previouslyFocused = document.activeElement;
    const modalEl = modalRef.current;
    if (modalEl) modalEl.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        closeModal();
      } else if (e.key === "ArrowLeft") {
        move(-1);
      } else if (e.key === "ArrowRight") {
        move(1);
      } else if (e.key === "Tab" && modalEl) {
        const focusable = modalEl.querySelectorAll("button:not([disabled])");
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [selectedIndex, closeModal, move]);

  const touchStartX = React.useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) move(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      navigate(`${basePath}/${newPage}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisible + 1);
    }
    if (start > 1) {
      pages.push(
        <PageNumber key={1} onClick={() => handlePageChange(1)}>
          1
        </PageNumber>,
      );
      if (start > 2) pages.push(<Ellipsis key="s">…</Ellipsis>);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <PageNumber key={i} $active={currentPage === i} onClick={() => handlePageChange(i)}>
          {i}
        </PageNumber>,
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<Ellipsis key="e">…</Ellipsis>);
      pages.push(
        <PageNumber key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </PageNumber>,
      );
    }
    return (
      <PaginationContainer>
        <PageButton
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          &lt;
        </PageButton>
        {pages}
        <PageButton
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
          aria-label="Next page"
        >
          &gt;
        </PageButton>
      </PaginationContainer>
    );
  };

  if (!ready) {
    return (
      <LoadingContainer>
        <LoadingText>
          {loadingText} {dots}
        </LoadingText>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  return (
    <>
      <GalleryContainer>
        {pageKeys.map((key, index) => (
          <GalleryImageCard
            key={key}
            imageKey={key}
            index={index}
            onClick={() => setSelectedIndex(index)}
          />
        ))}
      </GalleryContainer>

      {totalPages > 1 && renderPagination()}

      {selectedIndex !== null && (
        <Modal
          ref={modalRef}
          tabIndex={-1}
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer. Click the backdrop or press Escape to close."
        >
          <NavArrow
            $side="left"
            disabled={selectedIndex === 0}
            onClick={(e) => {
              e.stopPropagation();
              move(-1);
            }}
            aria-label="Previous image"
          >
            &lt;
          </NavArrow>
          <Figure onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <LightboxImage imageKey={pageKeys[selectedIndex]} />
            <Counter>
              {selectedIndex + 1} / {pageKeys.length}
            </Counter>
          </Figure>
          <NavArrow
            $side="right"
            disabled={selectedIndex === pageKeys.length - 1}
            onClick={(e) => {
              e.stopPropagation();
              move(1);
            }}
            aria-label="Next image"
          >
            &gt;
          </NavArrow>
        </Modal>
      )}
    </>
  );
};

// The lightbox media: an image, or a looping video. A video artefact is
// presentation, not a player: no controls, so it cannot be paused, scrubbed,
// muted/unmuted or picture-in-pictured. It loops for as long as the lightbox is
// open and stops when it closes (unmount). Same format fallback as the grid.
const LightboxImage = ({ imageKey }) => {
  const { current, onError } = useMediaFallback(imageKey);
  if (!current) return null;
  if (current.type === "video") {
    return (
      <ModalVideo
        as="video"
        ref={primeVideo}
        src={current.url}
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
        onClick={(e) => e.stopPropagation()}
        onError={onError}
      />
    );
  }
  return (
    <ModalImage
      src={current.url}
      alt={`Artefact ${imageKey}`}
      onClick={(e) => e.stopPropagation()}
      onError={onError}
    />
  );
};

// A grid tile: an image, or a muted looping autoplay video. Both fade in and use
// the same card styling; format fallback picks whichever exists.
const GalleryImageCard = ({ imageKey, index, onClick }) => {
  const { current, errored, onError } = useMediaFallback(imageKey);
  const currentUrl = current ? current.url : null;
  // `mediaRef` also catches media the browser had already cached, whose load
  // event fires before React can listen (see useMediaReady).
  const { ready: loaded, markReady, mediaRef } = useMediaReady(currentUrl);

  const settled = loaded || errored;
  const delay = { animationDelay: `${Math.min(index, 11) * STAGGER_MS}ms` };
  return (
    <GalleryCard
      className={loaded ? "loaded" : ""}
      onClick={loaded ? onClick : undefined}
      $loaded={settled}
    >
      {current &&
        (current.type === "video" ? (
          <GalleryImage
            as="video"
            ref={mediaRef}
            style={delay}
            src={current.url}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            onLoadedData={markReady}
            onError={onError}
          />
        ) : (
          <GalleryImage
            ref={mediaRef}
            style={delay}
            src={current.url}
            alt={`Artefact ${imageKey}`}
            loading="lazy"
            onLoad={markReady}
            onError={onError}
          />
        ))}
    </GalleryCard>
  );
};

export default PhotoGallery;

/* --------------------------------- styles -------------------------------- */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const shimmer = keyframes`
  from { background-position: -200% 0; }
  to { background-position: 200% 0; }
`;
const modalFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const GalleryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--gap);
  padding: var(--gap);
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryCard = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  background: var(--skeleton);
  background-image: linear-gradient(
    90deg,
    var(--skeleton) 25%,
    var(--skeleton-sheen) 37%,
    var(--skeleton) 63%
  );
  background-size: 200% 100%;
  animation: ${(p) => (p.$loaded ? "none" : shimmer)} 1.4s ease-in-out infinite;
  border-radius: var(--radius);
  overflow: hidden;
  cursor: ${(p) => (p.$loaded ? "pointer" : "default")};
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  transition: transform var(--dur) var(--ease), box-shadow var(--dur) var(--ease);

  &.loaded:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.4);
  }

  @media (hover: none) {
    &.loaded:hover {
      transform: none;
    }
  }
`;

const GalleryImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: transform var(--dur) var(--ease);

  ${GalleryCard}.loaded & {
    opacity: 1;
    animation: ${rise} 900ms var(--ease) backwards;
  }

  ${GalleryCard}.loaded:hover & {
    transform: scale(1.04);
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(12px);
  display: grid;
  grid-template-columns: 72px 1fr 72px;
  align-items: center;
  z-index: 2000;
  animation: ${modalFade} var(--dur-fast) ease;

  @media (max-width: 600px) {
    grid-template-columns: 48px 1fr 48px;
  }
`;

const Figure = styled.figure`
  grid-column: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  max-height: 100%;
  min-width: 0;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 82vh;
  object-fit: contain;
  border-radius: var(--radius-sm);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
`;

const ModalVideo = styled.video`
  max-width: 100%;
  max-height: 82vh;
  object-fit: contain;
  border-radius: var(--radius-sm);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
`;

const Counter = styled.figcaption`
  color: var(--paper);
  font-size: 14px;
  letter-spacing: 0.1em;
  opacity: 0.7;
`;

const NavArrow = styled.button`
  grid-row: 1;
  grid-column: ${(p) => (p.$side === "left" ? "1" : "3")};
  justify-self: center;
  height: 64px;
  width: 44px;
  background: none;
  border: none;
  color: var(--paper);
  font-size: 34px;
  font-weight: 900;
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? "0.25" : "0.85")};
  pointer-events: ${(p) => (p.disabled ? "none" : "auto")};
  transition: color var(--dur-fast) ease, opacity var(--dur-fast) ease;

  &:hover {
    color: var(--accent);
    opacity: 1;
  }

  @media (max-width: 600px) {
    font-size: 26px;
    width: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - var(--nav-h));
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
`;

const LoadingSpinner = styled.div`
  border: 6px solid #000;
  border-top: 6px solid var(--accent);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  animation: ${spin} 1s linear infinite;
  margin-top: 22px;
`;

const LoadingText = styled.div`
  color: var(--accent);
  width: 100%;
  text-align: center;
  font-size: 20px;
  padding: 0 16px;
  box-sizing: border-box;
  white-space: normal;
  max-width: 40ch;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 18px;
  }
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin: 36px 0 48px;
  flex-wrap: wrap;
  padding: 0 16px;
`;

const PageButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  color: ${(p) => (p.disabled ? "var(--muted)" : "var(--paper)")};
  pointer-events: ${(p) => (p.disabled ? "none" : "auto")};
  transition: color var(--dur-fast) ease;

  &:hover {
    color: var(--accent);
  }
`;

const PageNumber = styled.span`
  font-size: 18px;
  cursor: pointer;
  color: ${(p) => (p.$active ? "var(--accent)" : "var(--paper)")};
  font-weight: ${(p) => (p.$active ? "bold" : "normal")};
  transition: color var(--dur-fast) ease;

  &:hover {
    color: var(--accent);
  }
`;

const Ellipsis = styled.span`
  color: var(--muted);
`;
